import { motion } from "framer-motion";
import { useState } from "react";
import {
    Sparkle,
    TextT,
    Article,
    Hash,
    Target,
    DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    buildRsaCsv,
    buildKeywordsCsv,
    downloadCsv,
    rsaFilename,
    keywordsFilename,
    copyToClipboard,
} from "@/lib/csv";
import { CopyButton } from "@/components/CopyButton";
import { CampaignBuilderDialog } from "@/components/CampaignBuilderDialog";
import { LandingPageDialog } from "@/components/LandingPageDialog";
import { DataDrivenLandingDialog } from "@/components/DataDrivenLandingDialog";
import { CompetitorComparisonPanel } from "@/components/CompetitorComparisonPanel";
import { KeywordRefinementPanel } from "@/components/KeywordRefinementPanel";

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.06, duration: 0.32, ease: "easeOut" },
    }),
};

const Block = ({ icon, title, hint, copyAllText, copyAllLabel, testId, children }) => (
    <section className="flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b-2 border-ink">
            <div className="flex items-center gap-2 min-w-0">
                {icon}
                <h2 className="font-heading text-lg font-black uppercase tracking-tight truncate">
                    {title}
                </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {hint && <span className="mono-label">{hint}</span>}
                {copyAllText && (
                    <CopyButton
                        text={copyAllText}
                        label={copyAllLabel || "Copy all"}
                        variant="solid"
                        dataTestId={testId ? `${testId}-copy-all` : undefined}
                    />
                )}
            </div>
        </div>
        {children}
    </section>
);

const TAG_META = {
    urgency: { label: "Urgency", color: "bg-red-600 text-white border-red-700" },
    trust: { label: "Trust", color: "bg-emerald-600 text-white border-emerald-700" },
    price: { label: "Price", color: "bg-amber-500 text-ink border-amber-600" },
    local_keyword: { label: "Local + KW", color: "bg-klein text-white border-klein" },
};

// Highlight common CTA verb phrases inside description text.
const CTA_REGEX = /(book\s+now(?:\s*&?\s*save)?|book\s+today|get\s+(?:a\s+)?(?:free\s+)?quote|call\s+(?:us\s+)?(?:now|today)|call\s+today|request\s+a\s+quote|schedule\s+(?:online|now|today)|get\s+started|reserve\s+(?:your\s+)?spot|save\s+today|book\s+online|contact\s+us\s+today)/gi;

const highlightCta = (text) => {
    if (!text) return null;
    const parts = [];
    let last = 0;
    let match;
    CTA_REGEX.lastIndex = 0;
    while ((match = CTA_REGEX.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        parts.push(
            <mark
                key={`cta-${match.index}`}
                className="bg-klein/10 text-klein font-bold px-0.5 rounded"
            >
                {match[0]}
            </mark>
        );
        last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? parts : text;
};

export const AiInsightsPanel = ({ ai, result, adhocScrapes = [] }) => {
    const [refinedKeywords, setRefinedKeywords] = useState([]);

    const handleExportRsa = () => {
        try {
            downloadCsv(rsaFilename(result), buildRsaCsv(result));
            toast.success("RSA CSV downloaded — paste into Google Ads Editor → Ads");
        } catch (e) {
            toast.error("Failed to build RSA CSV");
        }
    };

    const handleExportKeywords = () => {
        try {
            downloadCsv(keywordsFilename(result), buildKeywordsCsv(result));
            toast.success("Keywords CSV downloaded — paste into Google Ads Editor → Keywords");
        } catch (e) {
            toast.error("Failed to build keywords CSV");
        }
    };

    const headlinesAll = (ai.headlines || []).join("\n");
    const descriptionsAll = (ai.descriptions || []).join("\n\n");
    const keywordsAll = (ai.keywords || []).join("\n");

    return (
        <div className="p-6 lg:p-10 flex flex-col gap-12 bg-aisurface dot-grid relative">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 border border-ink px-3 py-2 bg-white">
                    <Sparkle size={14} weight="fill" className="text-klein" />
                    <span className="mono-label" style={{ color: "#0A0A0A" }}>
                        AI Insights · claude sonnet 4.5
                    </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="export-rsa-csv"
                        onClick={handleExportRsa}
                        className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                        title="Google Ads Editor — Ads import"
                    >
                        <DownloadSimple size={13} weight="bold" />
                        RSA CSV
                    </button>
                    <button
                        data-testid="export-keywords-csv"
                        onClick={handleExportKeywords}
                        className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                        title="Google Ads Editor — Keywords import"
                    >
                        <DownloadSimple size={13} weight="bold" />
                        Keywords CSV
                    </button>
                    <CampaignBuilderDialog result={result} refinedKeywords={refinedKeywords} />
                    <LandingPageDialog result={result} />
                    <DataDrivenLandingDialog result={result} />
                </div>
            </div>

            {/* Competitor Comparison (only when we have competitor pages, either from search input or ad-hoc scrape) */}
            <CompetitorComparisonPanel
                pages={result?.competitor_pages || []}
                comparison={result?.competitor_comparison || null}
                adhocPages={adhocScrapes}
            />

            {/* Keyword Refinement — search volume, bid estimates, brand exclusion, intent filtering */}
            <KeywordRefinementPanel
                result={result}
                onApplyRefined={(kws) => setRefinedKeywords(kws)}
            />

            {/* Ready-to-Use Ad Copy — headlines with framework tags */}
            <Block
                icon={<TextT size={18} weight="bold" />}
                title="Ready-to-Use Ad Copy · Headlines"
                hint="urgency · trust · price · local×2"
                copyAllText={headlinesAll}
                copyAllLabel="Copy all headlines"
                testId="ai-headlines"
            >
                <ol className="flex flex-col" data-testid="ai-headlines">
                    {(() => {
                        // Prefer tagged_headlines; fall back to flat list.
                        const framework = ["urgency", "trust", "price", "local_keyword", "local_keyword"];
                        const rows = (ai.tagged_headlines && ai.tagged_headlines.length
                            ? ai.tagged_headlines
                            : (ai.headlines || []).map((h, i) => ({ text: h, tag: framework[i] || "trust" }))
                        );
                        return rows.map((row, i) => {
                            const meta = TAG_META[row.tag] || TAG_META.trust;
                            return (
                                <motion.li
                                    key={i}
                                    custom={i}
                                    initial="hidden"
                                    animate="show"
                                    variants={fadeUp}
                                    data-testid={`ai-headline-${i}`}
                                    className="group flex items-center justify-between gap-3 py-3 border-b border-neutral-300 last:border-b-0"
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <span className="mono-label shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span
                                            data-testid={`ai-headline-tag-${i}`}
                                            className={`shrink-0 border font-mono uppercase text-[9px] tracking-widest font-bold px-1.5 py-0.5 ${meta.color}`}
                                        >
                                            {meta.label}
                                        </span>
                                        <span className="font-heading text-base font-bold text-ink truncate">
                                            {row.text}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <span className="mono-label">{row.text.length}/30</span>
                                        <CopyButton
                                            text={row.text}
                                            label="Copy headline"
                                            dataTestId={`ai-headline-copy-${i}`}
                                        />
                                    </div>
                                </motion.li>
                            );
                        });
                    })()}
                </ol>
            </Block>

            {/* Descriptions — every one has a CTA */}
            <Block
                icon={<Article size={18} weight="bold" />}
                title="Ready-to-Use Ad Copy · Descriptions"
                hint="max 90 chars · CTA in every line"
                copyAllText={descriptionsAll}
                copyAllLabel="Copy all descriptions"
                testId="ai-descriptions"
            >
                <ol className="flex flex-col" data-testid="ai-descriptions">
                    {(ai.descriptions || []).map((d, i) => (
                        <motion.li
                            key={i}
                            custom={i}
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            data-testid={`ai-description-${i}`}
                            className="group py-4 border-b border-neutral-300 last:border-b-0"
                        >
                            <div className="flex items-center justify-between gap-3 mb-1">
                                <span className="mono-label">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div className="flex items-center gap-1">
                                    <span className="mono-label">{d.length}/90</span>
                                    <CopyButton
                                        text={d}
                                        label="Copy description"
                                        dataTestId={`ai-description-copy-${i}`}
                                    />
                                </div>
                            </div>
                            <p className="text-base text-ink leading-relaxed">
                                {highlightCta(d)}
                            </p>
                        </motion.li>
                    ))}
                </ol>
            </Block>

            {/* Keywords */}
            <Block
                icon={<Hash size={18} weight="bold" />}
                title="Likely Competitor Keywords"
                hint={`${(ai.keywords || []).length} inferred`}
                copyAllText={keywordsAll}
                copyAllLabel="Copy all keywords"
                testId="ai-keywords"
            >
                <div className="flex flex-wrap gap-2" data-testid="ai-keywords">
                    {(ai.keywords || []).map((k, i) => (
                        <motion.button
                            key={i}
                            custom={i}
                            initial="hidden"
                            animate="show"
                            variants={fadeUp}
                            data-testid={`ai-keyword-${i}`}
                            title="Click to copy"
                            onClick={async (e) => {
                                e.preventDefault();
                                const ok = await copyToClipboard(k);
                                if (ok) toast.success(`Copied "${k}"`);
                            }}
                            className="border border-ink bg-white hover:bg-ink hover:text-white transition-colors duration-150 px-2.5 py-1 font-mono text-xs text-ink cursor-pointer"
                        >
                            {k}
                        </motion.button>
                    ))}
                </div>
            </Block>

            {/* Gap Analysis */}
            <Block
                icon={<Target size={18} weight="bold" />}
                title="Competitive Gap Analysis"
                hint="angles to exploit"
                copyAllText={ai.gap_analysis || ""}
                copyAllLabel="Copy analysis"
                testId="ai-gap-analysis"
            >
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    data-testid="ai-gap-analysis"
                    className="text-base leading-relaxed text-ink bg-white border border-neutral-300 p-5"
                >
                    {ai.gap_analysis}
                </motion.p>
            </Block>
        </div>
    );
};
